import { IssueType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/issue_pb';
import { TFunction } from 'i18next';
import { assertUnreachable } from '~/types/assert-type';

export function issueShortLabel(t: TFunction, type: IssueType) {
  switch (type) {
    case IssueType.DEVICE_OFFLINE:
      return t('system.issue.labels.deviceOffline');
    case IssueType.BATTERY_IS_LOW:
      return t('system.issue.labels.batteryLow');
    case IssueType.WATER_HEIGHT_SENSOR_BROKEN:
      return t('system.issue.labels.waterSensorBroken');
    case IssueType.TEMPERATURE_SENSOR_BROKEN:
      return t('system.issue.labels.temperatureSensorBroken');
    case IssueType.VALVE_ACTUATOR_BROKEN:
      return t('system.issue.labels.valveActuatorBroken');
    case IssueType.ROOF_DRY:
      return t('system.issue.labels.roofDry');
    case IssueType.UNSPECIFIED:
      return t('system.issue.labels.unknown');
    case IssueType.DRAIN_CLOGGED:
      return t('system.issue.labels.drainClogged');
    case IssueType.OVERFLOW_DRAIN_CLOGGED:
      return t('system.issue.labels.overflowDrainClogged');
    case IssueType.OVERFLOW_HEIGHT_TO_LOW:
      return t('system.issue.labels.overflowHeightTooLow');
    case IssueType.PUMP_BROKEN:
      return t('system.issue.labels.pumpBroken');
    case IssueType.RAIN_SENSOR_DATA_MISMATCH:
      return t('system.issue.labels.rainSensorDataMismatch');
    case IssueType.TAP_BROKEN:
      return t('system.issue.labels.tapBroken');
    case IssueType.WATER_HEIGHT_SENSOR_FLUCTUATIONS:
      return t('system.issue.labels.waterHeightSensorFluctuations');
    case IssueType.AUTO_MODE_LONG_DISABLED:
      return t('system.issue.labels.autoModeLongDisabled');
    default:
      try {
        return assertUnreachable(type);
      } catch (e) {
        return `Unsupported issue type ${type}`;
      }
  }
}

export function issueDescription(t: TFunction, type: IssueType) {
  switch (type) {
    case IssueType.DEVICE_OFFLINE:
      return t('system.event.labels.deviceOffline');
    case IssueType.BATTERY_IS_LOW:
      return t('system.event.labels.batteryLow');
    case IssueType.WATER_HEIGHT_SENSOR_BROKEN:
      return t('system.event.labels.waterSensorBroken');
    case IssueType.TEMPERATURE_SENSOR_BROKEN:
      return t('system.event.labels.temperatureSensorBroken');
    case IssueType.VALVE_ACTUATOR_BROKEN:
      return t('system.event.labels.valveActuatorBroken');
    case IssueType.ROOF_DRY:
      return t('system.event.labels.roofDry');
    case IssueType.UNSPECIFIED:
      return t('system.event.issue.labels.unknown');
    case IssueType.DRAIN_CLOGGED:
      return t('system.event.labels.drainClogged');
    case IssueType.OVERFLOW_DRAIN_CLOGGED:
      return t('system.event.labels.overflowDrainClogged');
    case IssueType.OVERFLOW_HEIGHT_TO_LOW:
      return t('system.event.labels.overflowHeightTooLow');
    case IssueType.PUMP_BROKEN:
      return t('system.event.labels.pumpBroken');
    case IssueType.RAIN_SENSOR_DATA_MISMATCH:
      return t('system.event.labels.rainSensorDataMismatch');
    case IssueType.TAP_BROKEN:
      return t('system.event.labels.tapBroken');
    case IssueType.WATER_HEIGHT_SENSOR_FLUCTUATIONS:
      return t('system.event.labels.waterHeightSensorFluctuations');
    case IssueType.AUTO_MODE_LONG_DISABLED:
      return t('system.event.labels.autoModeLongDisabled');
    default:
      try {
        return assertUnreachable(type);
      } catch (e) {
        return `Unsupported issue type ${type}`;
      }
  }
}

export function issueImplications(t: TFunction, type: IssueType) {
  switch (type) {
    case IssueType.DEVICE_OFFLINE:
      return t('system.event.implications.deviceOffline');
    case IssueType.BATTERY_IS_LOW:
      return t('system.event.implications.batteryLow');
    case IssueType.WATER_HEIGHT_SENSOR_BROKEN:
      return t('system.event.implications.waterSensorBroken');
    case IssueType.TEMPERATURE_SENSOR_BROKEN:
      return t('system.event.implications.temperatureSensorBroken');
    case IssueType.VALVE_ACTUATOR_BROKEN:
      return t('system.event.implications.valveActuatorBroken');
    case IssueType.ROOF_DRY:
      return t('system.event.implications.roofDry');
    case IssueType.DRAIN_CLOGGED:
      return t('system.event.implications.drainClogged');
    case IssueType.OVERFLOW_DRAIN_CLOGGED:
      return t('system.event.implications.overflowDrainClogged');
    case IssueType.OVERFLOW_HEIGHT_TO_LOW:
      return t('system.event.implications.overflowHeightTooLow');
    case IssueType.WATER_HEIGHT_SENSOR_FLUCTUATIONS:
      return t('system.event.implications.waterHeightSensorFluctuations');
    case IssueType.PUMP_BROKEN:
      return t('system.event.implications.pumpBroken');
    case IssueType.TAP_BROKEN:
      return t('system.event.implications.tapBroken');
    case IssueType.RAIN_SENSOR_DATA_MISMATCH:
      return t('system.event.implications.rainSensorDataMismatch');
    case IssueType.UNSPECIFIED:
    default:
      return t('system.event.implications.unknown');
  }
}

export function issueRemediation(t: TFunction, type: IssueType) {
  switch (type) {
    case IssueType.DEVICE_OFFLINE:
      return t('system.event.remediation.deviceOffline');
    case IssueType.BATTERY_IS_LOW:
      return t('system.event.remediation.batteryLow');
    case IssueType.WATER_HEIGHT_SENSOR_BROKEN:
      return t('system.event.remediation.waterSensorBroken');
    case IssueType.TEMPERATURE_SENSOR_BROKEN:
      return t('system.event.remediation.temperatureSensorBroken');
    case IssueType.VALVE_ACTUATOR_BROKEN:
      return t('system.event.remediation.valveActuatorBroken');
    case IssueType.ROOF_DRY:
      return t('system.event.remediation.roofDry');
    case IssueType.DRAIN_CLOGGED:
      return t('system.event.remediation.drainClogged');
    case IssueType.OVERFLOW_DRAIN_CLOGGED:
      return t('system.event.remediation.overflowDrainClogged');
    case IssueType.RAIN_SENSOR_DATA_MISMATCH:
      return t('system.event.remediation.rainSensorDataMismatch');
    case IssueType.OVERFLOW_HEIGHT_TO_LOW:
      return t('system.event.remediation.overflowHeightTooLow');
    case IssueType.WATER_HEIGHT_SENSOR_FLUCTUATIONS:
      return t('system.event.remediation.waterHeightSensorFluctuations');
    case IssueType.TAP_BROKEN:
      return t('system.event.remediation.tapBroken');
    case IssueType.PUMP_BROKEN:
      return t('system.event.remediation.pumpBroken');
    case IssueType.UNSPECIFIED:
    default:
      return t('system.event.remediation.unknown');
  }
}
